import React, { memo, useMemo, useCallback } from "react";
import { Typography, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DeleteIcon,
  InboundIcon,
  OutboundIcon,
  SearchIcon,
} from "../../../components/common/iconography";
import { InboundState, InboundStatus } from "../../../utils/constants";
import { grey, green, red } from "@material-ui/core/colors";
import {
  getAccountColor,
  getProductColor,
  getProductName,
  getTrackCarDisplayFields,
  isRestrictedForCustomer,
} from "../../../utils/trackListUtils";
import {
  getSelectedRailcarContainerStyle,
  getSelectedRailcarIconColor,
  getSelectedRailcarSecondaryTextColor,
} from "../../../utils/selectedRailcarStyle";
import { isOutbound as isOutboundUtil } from "../../../utils/trackCarPersistence";

const useStyles = makeStyles((theme) => ({
  trackLocation: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    margin: theme.spacing(0.75, 0),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    border: "1px solid",
    borderColor: grey[600],
    borderRadius: theme.spacing(1),
    backgroundColor: "transparent",
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    backfaceVisibility: "hidden",
    "& *": {
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
    "&:hover": {
      cursor: "pointer",
    },
  },
  trackLocationCompact: {
    margin: theme.spacing(0.25, 0),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  trackLocationSelected: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    margin: theme.spacing(0.75, 0),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    ...getSelectedRailcarContainerStyle(theme),
    borderRadius: theme.spacing(1),
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    backfaceVisibility: "hidden",
    "& *": {
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
    "& $accountName": {
      color: getSelectedRailcarSecondaryTextColor(theme),
    },
    "& $carName svg": {
      color: getSelectedRailcarIconColor(),
    },
    "& $searchIcon": {
      color: getSelectedRailcarIconColor(),
    },
  },
  trackLocationSelectedCompact: {
    margin: theme.spacing(0.25, 0),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  trackLocationCompleted: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    margin: theme.spacing(0.75, 0),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    border: "1px solid",
    borderColor: green[600],
    borderRadius: theme.spacing(1),
    backgroundColor: "transparent",
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    backfaceVisibility: "hidden",
    "& *": {
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
  },
  trackLocationCompletedCompact: {
    margin: theme.spacing(0.25, 0),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  trackLocationReleased: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    margin: theme.spacing(0.75, 0),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    border: "1px solid",
    borderColor: red[600],
    borderRadius: theme.spacing(1),
    backgroundColor: "transparent",
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    backfaceVisibility: "hidden",
    "& *": {
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
  },
  trackLocationReleasedCompact: {
    margin: theme.spacing(0.25, 0),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  trackLocationAccountMatched: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    margin: theme.spacing(0.75, 0),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    ...getSelectedRailcarContainerStyle(theme),
    borderRadius: theme.spacing(1),
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    backfaceVisibility: "hidden",
    "& *": {
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
    "& $accountName": {
      color: getSelectedRailcarSecondaryTextColor(theme),
    },
    "& $carName svg": {
      color: getSelectedRailcarIconColor(),
    },
    "& $searchIcon": {
      color: getSelectedRailcarIconColor(),
    },
  },
  trackLocationAccountMatchedCompact: {
    margin: theme.spacing(0.25, 0),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  trackLocationFound: {
    ...getSelectedRailcarContainerStyle(theme),
    "& $accountName": {
      color: getSelectedRailcarSecondaryTextColor(theme),
    },
    "& $carName svg": {
      color: getSelectedRailcarIconColor(),
    },
    "& $searchIcon": {
      color: getSelectedRailcarIconColor(),
    },
  },
  dragging: {
    opacity: 0.1,
    willChange: "transform",
  },
  overlay: {
    cursor: "grabbing",
    zIndex: 9999,
    boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
    transition: "none !important",
  },
  grab: {
    cursor: "grab",
  },
  carName: {
    display: "flex",
    alignItems: "center",
    paddingTop: theme.spacing(0.5),
    paddingBottom: 0,
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  carNameCompact: {
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    fontSize: "0.75rem",
  },
  accountName: {
    fontSize: "0.65rem",
    color: theme.palette.text.secondary,
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(3), // Offset for the icon
    marginTop: theme.spacing(-0.5),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },

  accountIndicator: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: theme.spacing(1),
    zIndex: 1,
  },
  productIndicator: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: theme.spacing(1),
    zIndex: 1,
  },
  greenIcon: {
    color: green[600],
  },
  redIcon: {
    color: red[600],
  },
  greyIcon: {
    color: grey[600],
  },
  selectedIcon: {
    color: getSelectedRailcarIconColor(),
  },
  actionIcons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  searchIcon: {
    color: theme.palette.secondary.main,
  },
  selectionBadge: {
    position: "absolute",
    top: -12,
    right: -12,
    backgroundColor: theme.palette.secondary.main,
    color: "#fff",
    borderRadius: "50%",
    width: 26,
    height: 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    fontWeight: "bold",
    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
    zIndex: 1000,
    border: "2px solid #fff",
  },
}));

export const RailcarCard = memo(
  ({
    shipment,
    index,
    trackLocationId,
    isSelected,
    isAccountMatched,
    showSearchIcon,
    canDelete,
    onShipmentClick,
    onDelete,
    isOverlay = false,
    compactMode = false,
    loadedAccountIds, // Pass from parent to decouple from context
    selectionCount = 0, // Only pass the count, not the whole array
    isDraggingGlobal = false, // Suppress expensive UI during drag
    onMouseEnter,
    onMouseLeave,
    readonly = false,
    isCustomer = false,
    customerAccountId = null,
  }) => {
    const classes = useStyles();
    const showSelectionBadge = isOverlay && selectionCount > 1 && isSelected;
    const isRestricted = isRestrictedForCustomer(
      shipment,
      isCustomer,
      customerAccountId,
    );
    const showDetails = !isRestricted;
    const showFoundIndicator = showDetails && showSearchIcon;
    const isHighlighted = isSelected || isAccountMatched || showFoundIndicator;
    const displayFields = getTrackCarDisplayFields(shipment);
    const shipmentDetails = displayFields.shipment || {};

    // Priority: 1. Global Color Map (Source of Truth), 2. TrackCar field, 3. Null
    const accountColor = useMemo(() => {
      if (isRestricted) return null;
      const globalColor =
        displayFields.accountId &&
        loadedAccountIds?.has(displayFields.accountId)
          ? getAccountColor(displayFields.accountId)
          : null;
      return globalColor;
    }, [displayFields.accountId, loadedAccountIds, isRestricted]);

    // Product color: inline from car/shipment first, then productId map (no commodity fallback).
    const productColor = useMemo(() => {
      if (isRestricted) return null;
      const globalColor = getProductColor(displayFields.productId);
      return displayFields.productColor || globalColor || null;
    }, [
      displayFields.productColor,
      displayFields.productId,
      isRestricted,
    ]);

    const productName = useMemo(() => {
      if (isRestricted) return null;
      return (
        displayFields.productName ||
        getProductName(displayFields.productId)
      );
    }, [
      displayFields.productName,
      displayFields.productId,
      isRestricted,
    ]);

    const isOutbound = isOutboundUtil(shipment);

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: shipment.id,
      data: {
        type: "railcar",
        shipment,
        trackLocationId,
      },
      disabled: readonly,
    });

    const style = useMemo(
      () => ({
        transform: CSS.Translate.toString(transform),
        transition: isDragging ? "none" : transition,
        touchAction: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
        "--account-color": accountColor || "transparent",
        "--product-color": productColor || "transparent",
      }),
      [transform, transition, isDragging, accountColor, productColor],
    );

    const containerClassName = useMemo(() => {
      let baseClass = classes.trackLocation;
      let compactClass = compactMode ? classes.trackLocationCompact : "";

      if (isSelected) {
        baseClass = classes.trackLocationSelected;
        compactClass = compactMode ? classes.trackLocationSelectedCompact : "";
      } else if (showDetails && isAccountMatched) {
        baseClass = classes.trackLocationAccountMatched;
        compactClass = compactMode
          ? classes.trackLocationAccountMatchedCompact
          : "";
      } else if (
        showDetails &&
        shipmentDetails.status === InboundStatus.RELEASED
      ) {
        baseClass = classes.trackLocationReleased;
        compactClass = compactMode ? classes.trackLocationReleasedCompact : "";
      } else if (
        showDetails &&
        shipmentDetails.state === InboundState.COMPLETED
      ) {
        baseClass = classes.trackLocationCompleted;
        compactClass = compactMode ? classes.trackLocationCompletedCompact : "";
      }

      const foundClass = showFoundIndicator ? classes.trackLocationFound : "";
      const grabClass = isOverlay ? classes.overlay : classes.grab;
      const dragClass = isDragging ? classes.dragging : "";

      return `${baseClass} ${compactClass} ${foundClass} ${grabClass} ${dragClass}`;
    }, [
      isSelected,
      isAccountMatched,
      shipmentDetails.status,
      shipmentDetails.state,
      showFoundIndicator,
      isDragging,
      isOverlay,
      compactMode,
      classes,
      showDetails,
    ]);

    const iconClassName = useMemo(() => {
      if (isHighlighted) return classes.selectedIcon;
      if (shipmentDetails.status === InboundStatus.RELEASED)
        return classes.redIcon;
      if (shipmentDetails.state === InboundState.COMPLETED)
        return classes.greenIcon;
      return classes.greyIcon;
    }, [
      shipmentDetails.status,
      shipmentDetails.state,
      isHighlighted,
      classes.selectedIcon,
      classes.redIcon,
      classes.greenIcon,
      classes.greyIcon,
    ]);

    const carNameClassName = `${classes.carName} ${
      compactMode ? classes.carNameCompact : ""
    }`;

    const handleDelete = useCallback(
      (event) => {
        event.stopPropagation();
        onDelete(shipment, index);
      },
      [onDelete, shipment, index],
    );

    const handleClick = useCallback(
      (event) => {
        if (isDragging || isRestricted) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onShipmentClick(event, shipment, trackLocationId);
      },
      [onShipmentClick, shipment, trackLocationId, isDragging, isRestricted],
    );

    const handleMouseEnterLocal = useCallback(
      (e) => {
        if (!onMouseEnter) return;
        if (isRestricted) {
          onMouseEnter(e, displayFields.railCarNumber || "Unknown Railcar");
          return;
        }
        const title = `${displayFields.accountName || "Unknown Account"}${
          productName ? ` - ${productName}` : ""
        }`;
        onMouseEnter(e, title);
      },
      [onMouseEnter, displayFields, isRestricted, productName],
    );

    const Content = (
      <div
        className={containerClassName}
        onClick={handleClick}
        onMouseEnter={handleMouseEnterLocal}
        onMouseLeave={onMouseLeave}
      >
        {showDetails && accountColor && (
          <div
            className={classes.accountIndicator}
            style={{ backgroundColor: "var(--account-color)" }}
          />
        )}
        {showDetails && productColor && (
          <div
            className={classes.productIndicator}
            style={{ backgroundColor: "var(--product-color)" }}
          />
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            overflow: "hidden",
            position: "relative",
            zIndex: 2,
            minWidth: 0,
          }}
        >
          <Typography variant="body2" className={carNameClassName}>
            {showDetails &&
              (isOutbound ? (
                <OutboundIcon className={iconClassName} />
              ) : (
                <InboundIcon className={iconClassName} />
              ))}
            {showDetails && " "}
            {`${index + 1}.`} {`${displayFields.railCarNumber}`}
          </Typography>

          {showDetails && !compactMode && displayFields.accountName && (
            <Typography variant="caption" className={classes.accountName}>
              {displayFields.accountName}
            </Typography>
          )}
        </div>
        {!isOverlay && !readonly && (
          <span className={classes.actionIcons}>
            {showFoundIndicator && (
              <SearchIcon className={classes.searchIcon} />
            )}
            {canDelete && (
              <IconButton
                size="small"
                onClick={handleDelete}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <DeleteIcon color="error" />
              </IconButton>
            )}
          </span>
        )}
      </div>
    );

    if (isOverlay) {
      return (
        <div style={{ ...style, position: "relative" }}>
          {showSelectionBadge && (
            <div className={classes.selectionBadge}>{selectionCount}</div>
          )}
          {Content}
        </div>
      );
    }

    return (
      <div
        id={`railcar-${shipment.id}`}
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
      >
        {Content}
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.isDraggingGlobal !== nextProps.isDraggingGlobal) return false;
    if (prevProps.isSelected !== nextProps.isSelected) return false;
    if (prevProps.selectionCount !== nextProps.selectionCount) return false;
    if (prevProps.index !== nextProps.index) return false;

    return (
      prevProps.shipment.id === nextProps.shipment.id &&
      (prevProps.shipment.inbound?.status ||
        prevProps.shipment.outbound?.status) ===
        (nextProps.shipment.inbound?.status ||
          nextProps.shipment.outbound?.status) &&
      (prevProps.shipment.inbound?.state ||
        prevProps.shipment.outbound?.state) ===
        (nextProps.shipment.inbound?.state ||
          nextProps.shipment.outbound?.state) &&
      prevProps.shipment.railCarNumber === nextProps.shipment.railCarNumber &&
      prevProps.shipment.parentType === nextProps.shipment.parentType &&
      prevProps.trackLocationId === nextProps.trackLocationId &&
      prevProps.isAccountMatched === nextProps.isAccountMatched &&
      prevProps.showSearchIcon === nextProps.showSearchIcon &&
      prevProps.canDelete === nextProps.canDelete &&
      prevProps.isOverlay === nextProps.isOverlay &&
      prevProps.compactMode === nextProps.compactMode &&
      prevProps.onShipmentClick === nextProps.onShipmentClick &&
      prevProps.onDelete === nextProps.onDelete &&
      prevProps.loadedAccountIds === nextProps.loadedAccountIds &&
      prevProps.onMouseEnter === nextProps.onMouseEnter &&
      prevProps.onMouseLeave === nextProps.onMouseLeave &&
      prevProps.readonly === nextProps.readonly &&
      prevProps.isCustomer === nextProps.isCustomer &&
      prevProps.customerAccountId === nextProps.customerAccountId
    );
  },
);

RailcarCard.displayName = "RailcarCard";
